/* Cài đặt chia lại khách — Gọi điện (Telesales) và Chat (Trực page) — dành cho Người vận hành */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Drawer, Empty, InputNumber, Radio, Select, Switch, Tabs, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, History, MessageCircle, Phone, Settings2, Shuffle, Timer, Users } from 'lucide-react';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import { isLeadTag, shortTag } from './logic';
import {
  CHAT_CHANNELS, DEFAULT_CHAT_REASSIGN, DEFAULT_REASSIGN, UNIT_LABEL, idleText, thresholdText,
  type Channel, type ChatReassignCfg, type IdleUnit, type ReassignCfg, type Staff,
} from './reassign';
import { useLeadStore } from './store';

const Section = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
    <h4 className="mb-3 flex items-center gap-2 text-[14px] font-bold text-gray-800 dark:text-gray-100">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#e8f4f7] text-[#3e91ab] dark:bg-[#3e91ab]/20">{icon}</span>
      {title}
    </h4>
    <div className="space-y-3 text-[13px] text-gray-700 dark:text-gray-300">{children}</div>
  </section>
);
const Row = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <div className="min-w-0">
      <div className="font-medium">{label}</div>
      {hint && <div className="text-[12px] text-gray-400">{hint}</div>}
    </div>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, '0')}:00` }));
const UNITS = (Object.keys(UNIT_LABEL) as IdleUnit[]).map((u) => ({ value: u, label: UNIT_LABEL[u] }));

/** Phần dùng chung giữa 2 tab */
type Common = Pick<ReassignCfg, 'enabled' | 'mode' | 'idle' | 'unit' | 'workHours' | 'startH' | 'endH' | 'includeUnassigned' | 'strategy' | 'pool' | 'maxTimes'>;

function Banner<T extends Common>({ c, set, title, desc }: { c: T; set: (p: Partial<T>) => void; title: string; desc: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#e8f4f7] p-4 dark:bg-[#3e91ab]/10">
      <div className="min-w-0">
        <div className="text-[14px] font-bold text-[#2c6b7f] dark:text-[#8cc8da]">{title}</div>
        <div className="text-[12.5px] text-gray-600 dark:text-gray-300">{desc}</div>
      </div>
      <Switch checked={c.enabled} onChange={(v) => set({ enabled: v } as Partial<T>)} />
    </div>
  );
}

function ModeSection<T extends Common>({ c, set }: { c: T; set: (p: Partial<T>) => void }) {
  return (
    <Section icon={<Settings2 size={15} />} title="Cách thực hiện">
      <Radio.Group value={c.mode} onChange={(e) => set({ mode: e.target.value } as Partial<T>)} className="flex flex-col gap-2">
        <Radio value="suggest"><b>Chỉ đề xuất</b> <span className="text-gray-500">— hiện danh sách khách quá thời gian, Người vận hành chọn người nhận rồi chia</span></Radio>
        <Radio value="auto"><b>Tự động chia</b> <span className="text-gray-500">— hệ thống tự chia ngay khi khách quá thời gian</span></Radio>
      </Radio.Group>
    </Section>
  );
}

function TimeRows<T extends Common>({ c, set, label }: { c: T; set: (p: Partial<T>) => void; label: string }) {
  return (
    <>
      <Row label={label}>
        <InputNumber min={1} max={999} value={c.idle} onChange={(v) => set({ idle: Number(v) || 1 } as Partial<T>)} className="w-[90px]" />
        <Select value={c.unit} onChange={(v: IdleUnit) => set({ unit: v } as Partial<T>)} options={UNITS} className="w-[90px]" />
      </Row>
      <Row label="Chỉ đếm trong giờ làm việc" hint="Ngoài giờ không tính vào thời gian chờ">
        <Switch size="small" checked={c.workHours} onChange={(v) => set({ workHours: v } as Partial<T>)} />
        {c.workHours && (
          <>
            <Select size="small" value={c.startH} onChange={(v) => set({ startH: v } as Partial<T>)} options={HOURS} className="w-[86px]" />
            <span className="text-gray-400">→</span>
            <Select size="small" value={c.endH} onChange={(v) => set({ endH: v } as Partial<T>)} options={HOURS} className="w-[86px]" />
          </>
        )}
      </Row>
    </>
  );
}

function WhoSection<T extends Common>({ c, set, staffs, who }: { c: T; set: (p: Partial<T>) => void; staffs: Staff[]; who: string }) {
  return (
    <Section icon={<Users size={15} />} title="Chia cho ai">
      <Radio.Group value={c.strategy} onChange={(e) => set({ strategy: e.target.value } as Partial<T>)} className="flex flex-col gap-2">
        <Radio value="least_load"><b>Người đang giữ ít khách nhất</b></Radio>
        <Radio value="round_robin"><b>Xoay vòng lần lượt</b></Radio>
      </Radio.Group>
      <div>
        <div className="mb-1 font-medium">{who} được nhận khách</div>
        <Select
          mode="multiple"
          allowClear
          value={c.pool}
          onChange={(v) => set({ pool: v } as Partial<T>)}
          options={staffs.map((s) => ({ value: s.id, label: s.name }))}
          optionFilterProp="label"
          placeholder={`Để trống = tất cả ${who}`}
          className="w-full"
          maxTagCount="responsive"
        />
        <div className="mt-1 text-[12px] text-gray-400">Không giao lại cho chính người đang giữ khách. Khi chia tay, Người vận hành vẫn chọn được bất kỳ ai.</div>
      </div>
      <Row label="Mỗi khách chia lại tối đa" hint="Quá số lần này thì giữ nguyên và báo Người vận hành xử lý">
        <InputNumber min={1} max={10} value={c.maxTimes} onChange={(v) => set({ maxTimes: Number(v) || 1 } as Partial<T>)} className="w-[70px]" />
        <span>lần</span>
      </Row>
    </Section>
  );
}

export default function ReassignSettings({ open, onClose, tags, staffs, pageStaffs, preview, chatPreview }: {
  open: boolean;
  onClose: () => void;
  tags: CustomerTagItem[];
  staffs: Staff[];
  pageStaffs: Staff[];
  /** Số khách đang đủ điều kiện nếu áp dụng cài đặt (tính trên danh sách đang tải) */
  preview?: (c: ReassignCfg) => number;
  chatPreview?: (c: ChatReassignCfg) => number;
}) {
  const saved = useLeadStore((s) => s.reassignCfg);
  const savedChat = useLeadStore((s) => s.chatCfg);
  const setCfg = useLeadStore((s) => s.setReassignCfg);
  const setChatCfg = useLeadStore((s) => s.setChatCfg);
  const log = useLeadStore((s) => s.reassignLog);
  const navigate = useNavigate();
  const [tab, setTab] = useState<'call' | 'chat'>('call');
  const [c, setC] = useState<ReassignCfg>(saved);
  const [ch, setCh] = useState<ChatReassignCfg>(savedChat);
  useEffect(() => { if (open) { setC(saved); setCh(savedChat); } }, [open, saved, savedChat]);
  const set = (p: Partial<ReassignCfg>) => setC((x) => ({ ...x, ...p }));
  const setChat = (p: Partial<ChatReassignCfg>) => setCh((x) => ({ ...x, ...p }));

  const stageOptions = tags.filter((t) => isLeadTag(t.name)).map((t) => ({ value: shortTag(t.name), label: t.name }));
  const hoursErr = (x: Common) => (x.workHours && x.endH <= x.startH ? 'Giờ kết thúc phải sau giờ bắt đầu.' : null);
  const errCall = !c.channels.length ? 'Chọn ít nhất một loại tương tác.' : !c.stages.length ? 'Chọn ít nhất một mã S áp dụng.' : hoursErr(c);
  const errChat = !ch.channels.length ? 'Chọn ít nhất một kênh chat.' : hoursErr(ch);
  const err = errCall ? `Gọi điện: ${errCall}` : errChat ? `Chat: ${errChat}` : null;
  const kindLog = log.filter((x) => (x.kind ?? 'call') === tab);

  const save = () => {
    if (err) return;
    setCfg(c);
    setChatCfg(ch);
    message.success('Đã lưu cài đặt chia lại khách');
    onClose();
  };

  const callTab = (
    <div className="space-y-4">
      <Banner c={c} set={set} title="Chia lại khi Telesales không tương tác" desc={<>Telesales được giao khách mà quá <b>{thresholdText(c)}</b> không gọi qua tổng đài / không chat → khách được chia cho Telesales khác.</>} />
      <fieldset disabled={!c.enabled} className={`space-y-4 ${c.enabled ? '' : 'pointer-events-none opacity-50'}`}>
        <ModeSection c={c} set={set} />
        <Section icon={<Timer size={15} />} title="Điều kiện chia lại">
          <TimeRows c={c} set={set} label="Không tương tác quá" />
          <Row label="Tính là có tương tác" hint="Mốc tính: lúc giao khách hoặc lần tương tác gần nhất">
            <Checkbox.Group value={c.channels} onChange={(v) => set({ channels: v as Channel[] })} options={[{ value: 'call', label: 'Gọi qua tổng đài' }, { value: 'chat', label: 'Chat / Zalo' }]} />
          </Row>
          <div>
            <div className="mb-1 font-medium">Áp dụng cho mã S</div>
            <Select mode="multiple" value={c.stages} onChange={(v) => set({ stages: v })} options={stageOptions} className="w-full" placeholder="Chọn mã S" maxTagCount="responsive" />
            <div className="mt-1 text-[12px] text-gray-400">Khách đã hẹn gọi lại hoặc đã có lịch hẹn luôn được bỏ qua.</div>
          </div>
          <Row label="Chia luôn khách chưa có Telesales" hint="Khách chưa phân quá thời gian trên cũng được giao">
            <Switch size="small" checked={c.includeUnassigned} onChange={(v) => set({ includeUnassigned: v })} />
          </Row>
        </Section>
        <WhoSection c={c} set={set} staffs={staffs} who="Telesales" />
      </fieldset>
      {!errCall && preview && <Alert type={preview(c) ? 'warning' : 'success'} showIcon message={c.enabled ? `Theo cài đặt này, ${preview(c)} khách đang tải đủ điều kiện chia lại Telesales.` : 'Đang tắt — không chia lại Telesales.'} />}
    </div>
  );

  const chatTab = (
    <div className="space-y-4">
      <Banner c={ch} set={setChat} title="Chia lại khi Trực page chưa trả lời chat" desc={<>Khách nhắn tin mà Trực page phụ trách chưa trả lời quá <b>{thresholdText(ch)}</b> → chat được chia cho Trực page khác.</>} />
      <fieldset disabled={!ch.enabled} className={`space-y-4 ${ch.enabled ? '' : 'pointer-events-none opacity-50'}`}>
        <ModeSection c={ch} set={setChat} />
        <Section icon={<Timer size={15} />} title="Điều kiện chia lại">
          <TimeRows c={ch} set={setChat} label="Khách chờ trả lời quá" />
          <Row label="Kênh chat áp dụng" hint="Theo nguồn khách nhắn vào">
            <Checkbox.Group value={ch.channels} onChange={(v) => setChat({ channels: v as string[] })} options={CHAT_CHANNELS} />
          </Row>
          <div className="text-[12px] text-gray-400">Mốc tính: tin khách đầu tiên chưa được trả lời. Trực page trả lời là dừng đếm; người mới nhận được tính lại từ lúc nhận.</div>
          <Row label="Chia luôn chat chưa có Trực page" hint="Khách nhắn vào mà chưa có người phụ trách">
            <Switch size="small" checked={ch.includeUnassigned} onChange={(v) => setChat({ includeUnassigned: v })} />
          </Row>
        </Section>
        <WhoSection c={ch} set={setChat} staffs={pageStaffs} who="Trực page" />
      </fieldset>
      {!errChat && chatPreview && <Alert type={chatPreview(ch) ? 'warning' : 'success'} showIcon message={ch.enabled ? `Theo cài đặt này, ${chatPreview(ch)} khách đang chờ trả lời đủ điều kiện chia lại.` : 'Đang tắt — không chia lại chat.'} />}
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={580}
      title={<span className="flex items-center gap-2"><Shuffle size={18} className="text-[#3e91ab]" />Cài đặt chia lại khách</span>}
      footer={(
        <div className="flex items-center justify-between gap-2">
          <Button onClick={() => (tab === 'call' ? setC(DEFAULT_REASSIGN) : setCh(DEFAULT_CHAT_REASSIGN))}>Khôi phục mặc định</Button>
          <div className="flex items-center gap-2">
            {err && <span className="text-[12px] text-red-500">{err}</span>}
            <Button onClick={onClose}>Huỷ</Button>
            <Button type="primary" disabled={!!err} onClick={save} className="!bg-[#3e91ab]">Lưu cài đặt</Button>
          </div>
        </div>
      )}
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as 'call' | 'chat')}
        items={[
          { key: 'call', label: <span className="flex items-center gap-1.5"><Phone size={14} />Gọi điện · Telesales</span>, children: callTab },
          { key: 'chat', label: <span className="flex items-center gap-1.5"><MessageCircle size={14} />Chat · Trực page</span>, children: chatTab },
        ]}
      />

      <div className="mt-4">
        <Section icon={<History size={15} />} title={`Lịch sử chia lại ${tab === 'call' ? 'Telesales' : 'chat'} gần đây`}>
          {kindLog.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có lần chia lại nào" /> : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {kindLog.slice(0, 5).map((x) => (
                <li key={x.id} className="flex items-center gap-2 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-gray-800 dark:text-gray-100">{x.customer}</div>
                    <div className="flex items-center gap-1 text-[12px] text-gray-500">
                      {x.from ?? 'Chưa phân'} <ArrowRight size={12} /> <b className="text-[#2c6b7f] dark:text-[#8cc8da]">{x.to}</b>
                      <span>· chờ {idleText(x.idle)}</span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <Tag color={x.mode === 'auto' ? 'blue' : 'default'} className="m-0">{x.mode === 'auto' ? 'Tự động' : x.by}</Tag>
                    <div className="mt-0.5 text-[11px] text-gray-400">{dayjs(x.at).format('HH:mm DD/MM')}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Button block onClick={() => { onClose(); navigate('/telesales/reassign-history'); }} icon={<History size={14} />}>
            Xem toàn bộ lịch sử chia lại ({log.length})
          </Button>
        </Section>
      </div>
    </Drawer>
  );
}
